import { AxiosProgressEvent } from "axios";
import api from "./Api";
import { handleApiError } from "../utils/apiUtils";

export interface ApiResponse<T> {
  result: T;
  isSuccess: boolean;
  message: string;
  statusCode: number;
}

const activity = process.env.REACT_APP_CASE_URL || "";
if (!activity) {
  throw new Error(
    "BASE_URL is undefined. Please set REACT_APP_PICKLIST_URL in your environment variables.",
  );
}

const caseActivityAPI = api(activity);

const getCaseActivityData = async (caseData: any): Promise<any> => {
  try {
    const response = await caseActivityAPI.post<ApiResponse<any>>(
      "/api/case/activity/detail",
      caseData,
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again.",
    );
  }
};

const getCaseActivitySummaryList = async (): Promise<any> => {
  try {
    const response = await caseActivityAPI.get<ApiResponse<any>>(
      "/api/case/activity/summary/list",
    );
    const list = response.data?.result.map((item: any) => ({
      value: item.activityId,
      label: item.activitySummary,
    }));
    return list;
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again.",
    );
  }
};

const getCaseActivityUsers = async (caseID: any): Promise<any> => {
  try {
    const response = await caseActivityAPI.get<ApiResponse<any>>(
      `/api/case/activity/users?caseinfoid=${caseID}`,
    );
    const list = response.data?.result.map((item: any) => ({
      value: item.userId,
      label: item.userFullName,
    }));
    return list;
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again.",
    );
  }
};

const getCaseNotesPopUpDetails = async (caseID: any): Promise<any> => {
  try {
    const response = await caseActivityAPI.get<ApiResponse<any>>(
      `/api/case/notes/list/popup?noteId=${caseID}`,
    );
    const updatedResult = response.data?.result.map((notes: any) => ({
      ...notes,
      fileNameNotes: notes.fileName,
      Notes_description: notes.description,
      downloadFile: notes.cloudLocation,
    }));

    return updatedResult;
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again.",
    );
  }
};

const downloadNotesFile = async (
  attachmentId: number,
  fileNameNew: string,
  onProgress?: (percent: number) => void,
): Promise<void> => {
  try {
    const response = await caseActivityAPI.get(
      `/api/case/notes/download?attachmentId=${attachmentId}`,
      {
        responseType: "blob",
        onDownloadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100,
            );
            onProgress(percentCompleted);
          }
        },
      },
    );

    const contentDisposition = response.headers["content-disposition"];
    let fileNameMatch =
      contentDisposition?.match(/filename\*=(?:UTF-8'')?([^;\n]+)/) ||
      contentDisposition?.match(/filename="?([^";\n]+)"?/);

    const fileName = fileNameMatch
      ? decodeURIComponent(fileNameMatch[1])
      : fileNameNew;

    const blobUrl = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const downloadZipFileForNotes = async (
  attachementIDs: string,
  onProgress?: (percent: number) => void,
): Promise<void> => {
  try {
    const response = await caseActivityAPI.get(
      `/api/case/notes/download-zip/${attachementIDs}`,
      {
        responseType: "blob",
        onDownloadProgress: (progressEvent: AxiosProgressEvent) => {
          if (progressEvent.total && onProgress) {
            const percentCompleted = Math.round(
              (progressEvent.loaded / progressEvent.total) * 100,
            );
            onProgress(percentCompleted);
          }
        },
      },
    );

    const contentDisposition = response.headers["content-disposition"];
    let fileNameMatch =
      contentDisposition?.match(/filename\*=(?:UTF-8'')?([^;\n]+)/) ||
      contentDisposition?.match(/filename="?([^";\n]+)"?/);

    const fileName = fileNameMatch
      ? decodeURIComponent(fileNameMatch[1])
      : "download.zip";

    const blobUrl = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    handleApiError(error);
    throw error;
  }
};

const getCaseActivitySummaryData = async (caseData: any): Promise<any> => {
  try {
    const response = await caseActivityAPI.post<ApiResponse<any>>(
      "/api/case/activity/summary",
      caseData,
    );
    const { isSuccess, statusCode, message, result } = response.data;
    return { isSuccess, statusCode, message, result };
  } catch (error) {
    console.error("Error adding a new user:", error);
    throw new Error(
      "Failed to add the user. Please check the data and try again.",
    );
  }
};

export {
  getCaseActivityData,
  getCaseActivitySummaryList,
  getCaseActivityUsers,
  getCaseNotesPopUpDetails,
  downloadNotesFile,
  downloadZipFileForNotes,
  getCaseActivitySummaryData,
};
